import axiosInstance from './AxiosInstance';

const UserAssignmentsService = {
  /**
   * Get all user assignments with optional filters
   * @param {Object} params - Query parameters (userId, roleId, companyId, branchId, status)
   * @returns {Promise} - API response
   */
  getUserAssignments: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/user-assignments', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching user assignments:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch user assignments";
      throw {
        message: errorMessage,
        status: error.response?.status
      };
    }
  },

  /**
   * Get a specific user assignment by ID
   * @param {number} id - Assignment ID
   * @returns {Promise} - API response
   */
  getUserAssignmentById: async (id) => {
    try {
      const response = await axiosInstance.get(`/user-assignments/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user assignment:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch user assignment";
      throw {
        message: errorMessage,
        status: error.response?.status
      };
    }
  },

  /**
   * Get assignments for current logged-in user
   * @param {number} userId - User ID
   * @returns {Promise} - API response with active assignments
   */
  getMyAssignments: async (userId) => {
    try {
      const response = await axiosInstance.get('/user-assignments', {
        params: {
          userId,
          status: 'Active'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching my assignments:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch my assignments";
      throw {
        message: errorMessage,
        status: error.response?.status
      };
    }
  },

  /**
   * Get assignments grouped by company and branch for context switching
   * @param {number} userId - User ID
   * @returns {Promise} - Structured data for context switching
   */
  getContextSwitchingData: async (userId) => {
    try {
      const response = await axiosInstance.get('/user-assignments', {
        params: {
          userId,
          status: 'Active'
        }
      });

      if (response.data.success) {
        // Group assignments by company
        const groupedData = {};
        
        response.data.data.forEach(assignment => {
          const companyId = assignment.companyId;
          const companyName = assignment.company?.name || 'No Company';
          
          if (!groupedData[companyId]) {
            groupedData[companyId] = {
              companyId: companyId,
              companyName: companyName,
              company: assignment.company,
              branches: []
            };
          }

          // Add branch if not already added
          if (assignment.branchId) {
            const existingBranch = groupedData[companyId].branches.find(
              b => b.branchId === assignment.branchId
            );

            if (!existingBranch) {
              groupedData[companyId].branches.push({
                branchId: assignment.branchId,
                branchName: assignment.branch?.branchName || 'Unknown Branch 1',
                branch: assignment.branch,
                assignmentId: assignment.id,
                roleId: assignment.roleId,
                roleName: assignment.role?.roleName || '',
                roleCode: assignment.role?.roleCode || '',
                isPrimary: assignment.isPrimary
              });
            }
          }
          else if(assignment.company && assignment.company.branches && assignment.company.branches.length > 0) {
            groupedData[companyId]['branches'] = assignment.company.branches.map(branch => ({
              branchId: branch.id,
              branchName: branch.branchName,
              branch: branch.branchName,
              assignmentId: assignment.id,
              roleId: assignment.roleId,
              roleName: assignment.role?.roleName || '',
              roleCode: assignment.role?.roleCode || '',
              isPrimary: assignment.isPrimary
            }));
          }
        });

        return {
          success: true,
          data: Object.values(groupedData),
          rawAssignments: response.data.data
        };
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching context switching data:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch context switching data";
      throw {
        message: errorMessage,
        status: error.response?.status
      };
    }
  }
};

export default UserAssignmentsService;
